import { Theme } from 'store/theme'

import { ChargifyJsLoadOptions, SubscriptionCoupon } from '@infura/packages.chargify'

const { darkTheme, lightTheme } = require('@metamask/design-tokens')

export const getChargifyBasePath = () => {
  switch (process.env.ENVIRONMENT) {
    case 'production':
      return 'https://infura.chargify.com'
    case 'staging':
      return 'https://infura-sandbox.chargify.com'
    default:
      return 'https://infura-sandbox.chargify.com'
  }
}

// Font is not getting loaded into chargify..
const defaultLoadConfig = (theme: Theme): ChargifyJsLoadOptions => {
  const themeConfig = theme === 'dark' ? darkTheme : lightTheme
  return {
    publicKey: process.env.CHARGIFY_PUBLIC_KEY!,
    serverHost: getChargifyBasePath(),
    style: {
      '#chargify-form': {
        color: themeConfig.colors.text.default,
        fontFamily: 'Euclid Circular B, sans-serif',
        fontSize: '16px',
        height: '48px',
        lineHeight: '40px',
        maxWidth: '100%',
        overflow: 'hidden',
        width: '90%'
      },
      input: {
        color: themeConfig.colors.text.default,
        fontFamily: 'Euclid Circular B, sans-serif',
        fontSize: '16px',
        paddingBottom: '1px',
        paddingTop: '2px',
        width: '100%'
      },
      label: {
        fontFamily: 'Euclid Circular B, sans-serif',
        fontSize: '11px',
        fontWeight: '500',
        paddingBottom: '1px',
        paddingTop: '2px'
      },
      message: { paddingBottom: '1px', paddingTop: '2px' },
      number: {
        width: '70%'
      }
    },
    type: 'card'
  }
}

let chargify: any

export const getChargify = () => {
  if (!chargify && !!window?.Chargify && typeof window !== 'undefined') {
    chargify = new window.Chargify()
  }

  return chargify
}

declare global {
  interface Window {
    Chargify?: any
  }
}

export const getChargifyInvoicePDFUrl = (url: string) => {
  const urlObj = new URL(url)
  // append pdf to make it easily downloadable
  urlObj.pathname += '.pdf'

  return urlObj.href
}

export const isChargifyCouponUsedUp = (subscriptionCoupons: Array<SubscriptionCoupon>, couponCode: string) => {
  const usedUpCoupon = subscriptionCoupons?.find(
    // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
    cpn => !cpn.recurring && cpn.use_count >= cpn.uses_allowed && cpn.code.toLowerCase() === couponCode.toLowerCase()
  )

  return !!usedUpCoupon
}

export type FormBackground = 'default' | 'alternative'

export const getChargifyLoadConfig = (theme: Theme, formBackground: FormBackground = 'default') => {
  const themeConfig = theme === 'dark' ? darkTheme : lightTheme

  const formBackgroundColour =
    formBackground === 'default' ? themeConfig.colors.background.default : themeConfig.colors.background.alternative

  const defaultStyle = {
    field: {
      borderRadius: '8px',
      marginBottom: '0px',
      paddingLeft: '8px',
      paddingRight: '8px',
      width: '100%'
    },
    input: {
      ':focus': {
        borderColor: `${themeConfig.colors.primary.default} !important`,
        borderWidth: '2px !important',
        margin: '-1px'
      },
      backgroundColor: themeConfig.colors.background.default,
      borderColor: themeConfig.colors.border.muted,
      borderWidth: '1px',
      color: themeConfig.colors.text.default,
      paddingBottom: '6px',
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingTop: '6px',
      width: '100%'
    },
    label: {
      color: themeConfig.colors.text.default,
      fontSize: '14px',
      lineHeight: '1.5',
      marginBottom: '8px',
      marginTop: '0px',
      paddingBottom: '0px',
      paddingTop: '0px'
    },
    message: {
      color: themeConfig.colors.error.default,
      fontSize: '10px',
      margin: '0',
      paddingBottom: '1px',
      paddingTop: '2px',
      position: 'absolute',
      whiteSpace: 'nowrap'
    }
  }

  const style = defaultStyle
  const dropdownStyle = {
    field: {
      backgroundColor: themeConfig.colors.background.default,
      border: '8px solid',
      borderBottom: 'none',
      borderColor: formBackgroundColour,
      borderRadius: '8px',
      borderTop: 'none'
    },
    input: {
      ...style.input,
      appearance: 'none',
      backgroundColor: themeConfig.colors.background.default
    },
    label: {
      ...style.label,
      backgroundColor: formBackgroundColour,
      marginBottom: '0px',
      paddingBottom: '8px'
    },
    message: {
      color: themeConfig.colors.error.default,
      fontSize: '10px',
      margin: '0',
      paddingBottom: '1px',
      paddingTop: '2px',
      position: 'absolute',
      whiteSpace: 'nowrap'
    }
  }

  const fieldsConfig = {
    address: {
      label: 'Address Line 1',
      placeholder: '1234 Hill St',
      required: true,
      selector: '#chargify7',
      style
    },
    address2: {
      label: 'Address Line 2',
      placeholder: 'Apt 5B',
      selector: '#chargify8',
      style: style
    },
    city: {
      label: 'City',
      placeholder: 'Austin',
      required: true,
      selector: '#chargify11',
      style: style
    },
    country: {
      label: 'Country',
      placeholder: 'Select your country',
      required: true,
      selector: '#chargify9',
      style: dropdownStyle
    },
    cvv: {
      label: 'CVV',
      placeholder: '123',
      required: true,
      selector: '#chargify4',
      style: style
    },
    firstName: {
      label: 'First Name',
      placeholder: 'John',
      required: true,
      selector: '#chargify5',
      style
    },
    lastName: {
      label: 'Last Name',
      placeholder: 'Doe',
      required: true,
      selector: '#chargify6',
      style
    },
    month: {
      label: 'Expiration Month',
      placeholder: 'MM',
      required: true,
      selector: '#chargify2',
      style
    },
    number: {
      label: 'Card Number',
      placeholder: 'xxxx xxxx xxxx xxxx',
      required: true,
      selector: '#chargify1',
      style
    },
    state: {
      label: 'State',
      placeholder: 'Select your region',
      required: true,
      selector: '#chargify10',
      style: dropdownStyle
    },
    year: {
      label: 'Expiration Year',
      placeholder: 'YYYY',
      required: true,
      selector: '#chargify3',
      style
    },
    zip: {
      label: 'Postal Code',
      placeholder: '10001',
      required: true,
      selector: '#chargify12',
      style
    }
  }

  return {
    ...defaultLoadConfig(theme),
    addressDropdowns: true,
    fields: fieldsConfig,
    hideCardImage: true,
    optionalLabel: '(optional)',
    requiredLabel: '*',
    type: 'card'
  }
}

export const checkIframes = (chargifyForm, setShowLoaders) => {
  const observer = new MutationObserver((mutationsList, observer) => {
    for (const mutation of mutationsList) {
      if (mutation.addedNodes.length) {
        const iframes = chargifyForm.current?.querySelectorAll('iframe')
        if (iframes && iframes.length === 12) {
          setTimeout(() => {
            iframes.forEach(iframe => {
              iframe.style.width = '100%'
            })
            // Once we have found and modified all iframes, we don't need the observer anymore
            observer.disconnect()
            setShowLoaders(false)
          }, 500)
        }
      }
    }
  })

  // Start observing the document with the configured parameters
  observer.observe(chargifyForm.current, { childList: true, subtree: true })
}
